const chapter = {
  id: 'ray-optics',
  slug: 'ray-optics',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Ray Optics & Optical Instruments',
        hindi: 'किरण प्रकाशिकी और प्रकाशिक यंत्र',
  },
  estReadMinutes: 70,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/एनईईटी में 3 प्रश्न',
  },
  syllabusTags: ['Optics & Modern', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'reflection',
      title: {
        english: 'Reflection at Spherical Mirrors',
        hindi: 'गोलीय दर्पणों पर परावर्तन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Laws: angle of incidence = angle of reflection; incident, reflected, normal coplanar.',
        hindi: 'कानून: incidence = angle of reflection का कोण; घटना, प्रतिबिंबित, सामान्य समतलीय।',
            },
            {
              english: 'Plane mirror: image is virtual, erect, same size, laterally inverted, distance behind = distance in front. To see full image, mirror length $\\ge$ half of object height.',
        hindi: 'समतल दर्पण: छवि आभासी, सीधी, समान आकार, पार्श्व रूप से उलटी, दूरी behind = distance in front है। पूर्ण छवि देखने के लिए, दर्पण की लंबाई वस्तु की ऊंचाई की आधी लंबाई $\\ge$ है।',
            },
            {
              english: 'Sign convention (Cartesian): incident light along +x, distances measured from pole; height up positive.',
        hindi: 'साइन कन्वेंशन (कार्टेशियन): +x के साथ आपतित प्रकाश, ध्रुव से मापी गई दूरी; ऊंचाई सकारात्मक.',
            },
            {
              english: '**Focal length** of spherical mirror: $f = R/2$. Concave $R, f$ negative; convex positive (with above convention).',
        hindi: '**गोलाकार दर्पण की फोकल लंबाई**: $f = R/2$। अवतल $R, f$ ऋणात्मक; उत्तल सकारात्मक (उपरोक्त परिपाटी के साथ)।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Mirror formula & magnification',
        hindi: 'दर्पण सूत्र और आवर्धन',
          },
          statement: {
            english: `$$\\dfrac{1}{v} + \\dfrac{1}{u} = \\dfrac{1}{f},\\qquad m = \\dfrac{h_i}{h_o} = -\\dfrac{v}{u}.$$
Real/inverted: $m < 0$. Virtual/erect: $m > 0$.`,
            hindi: '$$\\dfrac{1}{v} + \\dfrac{1}{u} = \\dfrac{1}{f},\\qquad m = \\dfrac{h_i}{h_o} = -\\dfrac{v}{u}.$$ Real/inverted: $m < 0$. Virtual/erect: $m > 0$. अपवर्तक सूचकांक $m < 0$ के माध्यम में अपवर्तक सूचकांक $m > 0$ के पतले लेंस के लिए: $$\\dfrac{1}{v} + \\dfrac{1}{u} = \\dfrac{1}{f},\\qquad m = \\dfrac{h_i}{h_o} = -\\dfrac{v}{u}.$$ हवा में (Real/inverted): Virtual/erect।',
          },
        },
      ],
    },
    {
      id: 'refraction',
      title: {
        english: 'Refraction & Snell\'s Law',
        hindi: 'अपवर्तन और स्नेल का नियम',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Snell\'s law',
        hindi: 'स्नेल का नियम',
          },
          statement: {
            english: '$n_1 \\sin\\theta_1 = n_2 \\sin\\theta_2$. Refractive index $n = c/v$. Optical path length $= n \\cdot $ geometrical length.',
        hindi: '$n_1 \\sin\\theta_1 = n_2 \\sin\\theta_2$. अपवर्तनांक $n = c/v$. ऑप्टिकल पथ की लंबाई $= n \\cdot $ ज्यामितीय लंबाई।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Apparent depth: $d_{\\text{app}} = d_{\\text{real}}/n$ (object in denser medium viewed from rarer).',
        hindi: 'स्पष्ट गहराई: $d_{\\text{app}} = d_{\\text{real}}/n$ (घने माध्यम में वस्तु को दुर्लभ से देखा गया)।',
            },
            {
              english: '**Total internal reflection** when light goes from denser → rarer at $\\theta > \\theta_c$, where $\\sin\\theta_c = n_2/n_1$ (for $n_1 > n_2$).',
        hindi: '**कुल आंतरिक परावर्तन** जब प्रकाश सघनता से जाता है → $\\theta > \\theta_c$ पर दुर्लभ, जहां $\\sin\\theta_c = n_2/n_1$ ($n_1 > n_2$ के लिए)।',
            },
            {
              english: 'TIR applications: optical fibre, sparkle of diamond ($n = 2.42$ ⇒ $\\theta_c \\approx 24°$), prism in periscope, mirage.',
        hindi: 'टीआईआर अनुप्रयोग: ऑप्टिकल फाइबर, हीरे की चमक ($n = 2.42$ ⇒ $\\theta_c \\approx 24°$), पेरिस्कोप में प्रिज्म, मृगतृष्णा।',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Refraction at single spherical surface',
        hindi: 'एकल गोलाकार सतह पर अपवर्तन',
          },
          statement: {
            english: '$\\dfrac{n_2}{v} - \\dfrac{n_1}{u} = \\dfrac{n_2 - n_1}{R}$.',
        hindi: '$\\dfrac{n_2}{v} - \\dfrac{n_1}{u} = \\dfrac{n_2 - n_1}{R}$.',
          },
        },
      ],
    },
    {
      id: 'lenses',
      title: {
        english: 'Thin Lenses & Lens Maker\'s Formula',
        hindi: 'पतले लेंस और लेंस निर्माता सूत्र',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Lens maker\'s formula',
        hindi: 'लेंस निर्माता का सूत्र',
          },
          statement: {
            english: `For a thin lens of refractive index $n$ in medium of refractive index $n_m$:
$$\\dfrac{1}{f} = \\left(\\dfrac{n}{n_m} - 1\\right)\\left(\\dfrac{1}{R_1} - \\dfrac{1}{R_2}\\right).$$
In air ($n_m = 1$): $1/f = (n - 1)(1/R_1 - 1/R_2)$.`,
            hindi: 'अपवर्तक सूचकांक $n$ के माध्यम में अपवर्तक सूचकांक $n_m$ के पतले लेंस के लिए: $$\\dfrac{1}{f} = \\left(\\dfrac{n}{n_m} - 1\\right)\\left(\\dfrac{1}{R_1} - \\dfrac{1}{R_2}\\right).$$ हवा में ($n_m = 1$): $1/f = (n - 1)(1/R_1 - 1/R_2)$।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Thin lens formula',
        hindi: 'पतला लेंस सूत्र',
          },
          statement: {
            english: '$\\dfrac{1}{v} - \\dfrac{1}{u} = \\dfrac{1}{f}$, $m = v/u$. Power $P = 1/f$ (in metres) → diopters (D).',
        hindi: '$\\dfrac{1}{v} - \\dfrac{1}{u} = \\dfrac{1}{f}$, $m = v/u$. पावर $P = 1/f$ (मीटर में) → डायोप्टर (डी)।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Lenses in contact: $1/f = 1/f_1 + 1/f_2 + \\ldots$, $P = P_1 + P_2 + \\ldots$.',
        hindi: 'संपर्क में लेंस: $1/f = 1/f_1 + 1/f_2 + \\ldots$, $P = P_1 + P_2 + \\ldots$।',
            },
            {
              english: 'Sign: convex lens $f > 0$, concave $f < 0$.',
        hindi: 'चिह्न: उत्तल लेंस $f > 0$, अवतल $f < 0$।',
            },
            {
              english: 'Silvering one face of a lens: equivalent mirror with $1/F = 2/f_l + 1/f_m$ (use power addition).',
        hindi: 'लेंस के एक पहलू को सिल्वर करना: $1/F = 2/f_l + 1/f_m$ के साथ समतुल्य दर्पण (पावर एडिशन का उपयोग करें)।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'A convex lens, $f = 20$ cm, with object at $30$ cm. Find image distance and magnification.',
        hindi: 'एक उत्तल लेंस, $f = 20$ सेमी, वस्तु $30$ सेमी पर। छवि की दूरी और आवर्धन ज्ञात करें।',
          },
          solution: {
            english: '$1/v = 1/f + 1/u = 1/20 - 1/30 = 1/60 \\Rightarrow v = 60$ cm. $m = v/u = -2$.',
        hindi: '$1/वी = 1/एफ + 1/यू = 1/20 - 1/30 = 1/60 \\Rightarrow वी = 60$ सेमी। $m = v/u = -2$.',
          },
        },
      ],
    },
    {
      id: 'prism',
      title: {
        english: 'Prism & Dispersion',
        hindi: 'प्रिज्म और वर्ण विक्षेपण',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Prism formula',
        hindi: 'प्रिज्म सूत्र **फैलाव**: (बैंगनी रंग अधिक झुकता है) → स्पेक्ट्रम। **कोणीय फैलाव** । **माध्य विचलन** .',
          },
          statement: {
            english: `For a prism of refracting angle $A$ and minimum deviation $D_m$:
$$n = \\dfrac{\\sin\\!\\left(\\dfrac{A + D_m}{2}\\right)}{\\sin(A/2)}.$$
For small-angle prism: $D \\approx (n - 1)A$.`,
            hindi: 'अपवर्तक कोण $A$ और न्यूनतम विचलन $D_m$ के प्रिज्म के लिए: $$n = \\dfrac{\\sin\\!\\left(\\dfrac{A + D_m}{2}\\right)}{\\sin(A/2)}.$$ लघु-कोण प्रिज्म के लिए: $D \\approx (n - 1)A$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Dispersion**: $n_v > n_r$ (violet bends more) → spectrum.',
        hindi: '**फैलाव**: $n_v > n_r$ (बैंगनी रंग अधिक झुकता है) → स्पेक्ट्रम।',
            },
            {
              english: '**Angular dispersion** $\\theta = (n_v - n_r)A$. **Mean deviation** $D = (n_y - 1)A$.',
        hindi: '**कोणीय फैलाव** $\\theta = (n_v - n_r)A$. **माध्य विचलन** $D = (n_y - 1)A$.',
            },
            {
              english: '**Dispersive power** $\\omega = (n_v - n_r)/(n_y - 1) = \\theta/D$.',
        hindi: '**फैलाने वाली शक्ति** $\\omega = (n_v - n_r)/(n_y - 1) = \\theta/D$।',
            },
            {
              english: 'Achromatic combination of two prisms: $\\omega_1/\\omega_2 = -D_2/D_1$.',
        hindi: 'दो प्रिज्मों का अक्रोमैटिक संयोजन: $\\omega_1/\\omega_2 = -D_2/D_1$।',
            },
          ],
        },
      ],
    },
    {
      id: 'instruments',
      title: {
        english: 'Optical Instruments — Eye, Microscope, Telescope',
        hindi: 'प्रकाशिक यंत्र — नेत्र, सूक्ष्मदर्शी, दूरबीन',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Near point** $D = 25$ cm. **Far point** of normal eye = $\\infty$.',
        hindi: '**निकट बिंदु** $D = 25$ सेमी. **सामान्य eye = का सुदूर बिंदु**।',
            },
            {
              english: '**Myopia** (short-sighted): far point < $\\infty$. Corrective lens: concave, $f = -$ far point.',
        hindi: '**मायोपिया** (अदूरदर्शी): दूर बिंदु < $\\infty$। सुधारात्मक लेंस: अवतल, $f = -$ सुदूर बिंदु।',
            },
            {
              english: '**Hypermetropia**: near point > 25 cm. Corrective: convex.',
        hindi: '**हाइपरमेट्रोपिया**: निकट बिंदु> 25 सेमी। सुधारात्मक: उत्तल.',
            },
            {
              english: '**Simple microscope** (single convex lens): $M = 1 + D/f$ (image at $D$); $M = D/f$ (image at $\\infty$).',
        hindi: '**सरल माइक्रोस्कोप** (एकल उत्तल लेंस): $M = 1 + D/f$ (छवि $D$ पर); $M = D/f$ (छवि $\\infty$ पर)।',
            },
            {
              english: '**Compound microscope**: $M = M_o \\cdot M_e \\approx -\\dfrac{L}{f_o}\\left(1 + \\dfrac{D}{f_e}\\right)$.',
        hindi: '**यौगिक सूक्ष्मदर्शी**: $M = M_o \\cdot M_e \\approx -\\dfrac{L}{f_o}\\left(1 + \\dfrac{D}{f_e}\\right)$.',
            },
            {
              english: '**Astronomical telescope** (normal adjustment, image at infinity): $M = -f_o/f_e$. Length $L = f_o + f_e$.',
        hindi: '**खगोलीय दूरबीन** (सामान्य समायोजन, अनंत पर छवि): $M = -f_o/f_e$। लंबाई $L = f_o + f_e$.',
            },
            {
              english: 'Resolving power: microscope $\\propto 2 n\\sin\\theta/\\lambda$ (numerical aperture). Telescope $\\propto D/(1.22\\lambda)$.',
        hindi: 'विभेदन शक्ति: माइक्रोस्कोप $\\propto 2 n\\sin\\theta/\\lambda$ (संख्यात्मक एपर्चर)। टेलीस्कोप $\\propto D/(1.22\\lambda)$.',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Astronomical telescope with $f_o = 100$ cm, $f_e = 5$ cm. Magnification (image at $\\infty$) and length?',
        hindi: '$f_o = 100$ सेमी, $f_e = 5$ सेमी के साथ खगोलीय दूरबीन। आवर्धन (छवि $\\infty$ पर) और लंबाई?',
          },
          solution: {
            english: '$M = -100/5 = -20$. $L = 100 + 5 = 105$ cm.',
        hindi: '$M = -100/5 = -20$. $L = 100 + 5 = 105$ सेमी.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '1/v + 1/u = 1/f\\ \\text{(mirror)}',
      desc: {
        english: 'Mirror formula',
        hindi: 'मिरर फॉर्मूला',
      },
    },
    {
      latex: '1/v - 1/u = 1/f\\ \\text{(lens)}',
      desc: {
        english: 'Lens formula',
        hindi: 'लेंस सूत्र',
      },
    },
    {
      latex: 'n_1 \\sin\\theta_1 = n_2\\sin\\theta_2',
      desc: {
        english: 'Snell',
        hindi: 'स्नेल',
      },
    },
    {
      latex: '\\sin\\theta_c = n_2/n_1',
      desc: {
        english: 'Critical angle',
        hindi: 'क्रांतिक कोण',
      },
    },
    {
      latex: '1/f = (n - 1)(1/R_1 - 1/R_2)',
      desc: {
        english: 'Lens maker',
        hindi: 'लेंस निर्माता',
      },
    },
    {
      latex: 'n = \\sin((A+D_m)/2)/\\sin(A/2)',
      desc: {
        english: 'Prism',
        hindi: 'प्रिज्म',
      },
    },
    {
      latex: 'M_{\\text{tele}} = -f_o/f_e',
      desc: {
        english: 'Telescope',
        hindi: 'टेलीस्कोप',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Lens',
      problem: {
        english: 'A convex lens of focal length 20 cm forms image of an object at 40 cm. Image distance?',
        hindi: '20 सेमी फोकल लंबाई का एक उत्तल लेंस 40 सेमी पर एक वस्तु की छवि बनाता है। छवि दूरी?',
      },
      answer: {
        english: '$40$ cm (real, inverted, same size)',
        hindi: '$40$ सेमी (वास्तविक, उल्टा, समान आकार)',
      },
      solution: {
        english: 'Object at $2f$: $v = 2f = 40$ cm.',
        hindi: 'वस्तु $2f$ पर: $v = 2f = 40$ सेमी.',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'TIR',
      problem: {
        english: 'Critical angle for glass ($n = 1.5$) in air?',
        hindi: 'हवा में कांच के लिए क्रांतिक कोण ($n = 1.5$)?',
      },
      answer: {
        english: '$\\sin^{-1}(2/3) \\approx 41.8°$',
        hindi: '$\\sin^{-1}(2/3) \\approx 41.8°$',
      },
      solution: {
        english: '$\\sin\\theta_c = 1/1.5 = 2/3$.',
        hindi: '$\\sin\\theta_c = 1/1.5 = 2/3$.',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Power',
      problem: {
        english: 'Two thin lenses $+5$ D and $-2$ D in contact. Combined power and focal length?',
        hindi: 'दो पतले लेंस $+5$ D और $-2$ D संपर्क में हैं। संयुक्त शक्ति और फोकल लंबाई?',
      },
      answer: {
        english: '$3$ D, $f = 33.3$ cm',
        hindi: '$3$ D, $f = 33.3$ सेमी',
      },
      solution: {
        english: '$P = P_1 + P_2 = 3$ D. $f = 100/3$ cm.',
        hindi: '$P = P_1 + P_2 = 3$ D. $f = 100/3$ सेमी.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Prism',
      problem: {
        english: 'Equilateral prism ($A = 60°$), $n = \\sqrt 3$. Minimum deviation?',
        hindi: 'समबाहु प्रिज्म ($A = 60°$), $n = \\sqrt 3$। न्यूनतम विचलन?',
      },
      answer: {
        english: '$60°$',
        hindi: '$60°$',
      },
      solution: {
        english: '$\\sin((60+D)/2)/\\sin 30° = \\sqrt 3 \\Rightarrow \\sin((60+D)/2) = \\sqrt 3/2 \\Rightarrow (60+D)/2 = 60° \\Rightarrow D = 60°$.',
        hindi: '$\\sin((60+D)/2)/\\sin 30° = \\sqrt 3 \\Rightarrow \\sin((60+D)/2) = \\sqrt 3/2 \\Rightarrow (60+D)/2 = 60° \\Rightarrow D = 60°$.',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Apparent depth',
      problem: {
        english: 'Object at depth 12 cm in water ($n = 4/3$). Apparent depth?',
        hindi: 'पानी में 12 सेमी गहराई पर वस्तु ($n = 4/3$)। स्पष्ट गहराई?',
      },
      answer: {
        english: '$9$ cm',
        hindi: '$9$ सेमी',
      },
      solution: {
        english: '$d/n = 12/(4/3) = 9$.',
        hindi: '$d/n = 12/(4/3) = 9$।',
      },
    },
  ],
  next: {
    slug: 'wave-optics',
    title: 'Wave Optics',
  },
};

export default chapter;
